import { hasGradebookRedesignFeature } from '@/composables/gradebook/hasGradebookRedesignFeature.js';

/**
 * Escape hatch so the legacy gradebook stays reachable while the redesign is live —
 * used by the side-by-side parity pass.
 *
 * Both guards propagate `query: to.query`, so a redirect DOES carry `ui=legacy` onward.
 * That is deliberate and load-bearing: dropping it would make flag-on + `ui=legacy` on a
 * redesign route ping-pong between the two guards.
 */
export const LEGACY_UI_QUERY = 'ui';
export const LEGACY_UI_VALUE = 'legacy';

/**
 * Named routes. The grader's parent record is unnamed, so the child name is what the
 * guards key on.
 *
 * NOTE: `gradebook-grid` and `gradebook-grader` do not exist yet — waves 3 and 4 add
 * them, along with the `beforeEnter` wiring that makes these guards run at all. Nothing
 * imports this module today except its test. Pushing to either name before then throws
 * `No match for {"name":"gradebook-grid"}`.
 */
export const LEGACY_GRADER_ROUTE = 'gradebook-activity-grader';
export const LEGACY_AUDIT_LOG_ROUTE = 'gradebook-audit-log';
export const REDESIGN_GRID_ROUTE = 'gradebook-grid';
export const REDESIGN_GRADER_ROUTE = 'gradebook-grader';

/**
 * The STANDALONE grader, reached from Submissions and the four remediation surfaces.
 *
 * It gets its own redesign record rather than redirecting into `gradebook-grader`, because
 * the two are gated differently: the standalone route requires `view grade`, the gradebook
 * one `view gradebook`. Folding them together would silently change who can open a grader
 * — in whichever direction the two abilities happen to differ for a given role.
 */
export const LEGACY_STANDALONE_GRADER_ROUTE = 'activity-grader-view';
export const REDESIGN_STANDALONE_GRADER_ROUTE = 'activity-grader-redesign';

/**
 * The audit log has no redesign ROUTE — it is a tab on the grid. It was left unredirected
 * until wave 3b built that tab, because sending it to the bare grid would have silently
 * swallowed the audit surface rather than redesigning it. Now the tab exists, so the
 * redirect carries a query that selects it.
 */
export const AUDIT_LOG_TAB_QUERY = { tab: 'audit' };

/** Legacy route -> its redesign counterpart. Each pair keeps its own authorisation. */
const REDESIGN_OF = Object.freeze({
    [LEGACY_GRADER_ROUTE]: REDESIGN_GRADER_ROUTE,
    [LEGACY_STANDALONE_GRADER_ROUTE]: REDESIGN_STANDALONE_GRADER_ROUTE,
});

const LEGACY_OF = Object.freeze({
    [REDESIGN_GRADER_ROUTE]: LEGACY_GRADER_ROUTE,
    [REDESIGN_STANDALONE_GRADER_ROUTE]: LEGACY_STANDALONE_GRADER_ROUTE,
});

function wantsLegacy(to) {
    return to?.query?.[LEGACY_UI_QUERY] === LEGACY_UI_VALUE;
}

/**
 * Guard for LEGACY routes: send the user to the redesign when the flag is on.
 *
 * @param {import('vue-router').RouteLocationNormalized} to
 * @returns {object|undefined} A route location to redirect to, or undefined to proceed.
 */
export function redirectToRedesign(to) {
    if (wantsLegacy(to) || !hasGradebookRedesignFeature()) {
        return undefined;
    }

    const grader = REDESIGN_OF[to?.name];

    if (grader) {
        return { name: grader, params: to.params, query: to.query };
    }

    if (to?.name === LEGACY_AUDIT_LOG_ROUTE) {
        return {
            name: REDESIGN_GRID_ROUTE,
            params: to.params,
            query: { ...to.query, ...AUDIT_LOG_TAB_QUERY },
        };
    }

    return { name: REDESIGN_GRID_ROUTE, params: to.params, query: to.query };
}

/**
 * Guard for REDESIGN routes: send the user back to legacy when the flag is off.
 *
 * The two guards are exact complements — redesign routes redirect only when the flag
 * is off, legacy routes only when it is on — so they cannot form a loop.
 *
 * @param {import('vue-router').RouteLocationNormalized} to
 * @returns {object|undefined} A route location to redirect to, or undefined to proceed.
 */
export function redirectToLegacy(to) {
    if (!wantsLegacy(to) && hasGradebookRedesignFeature()) {
        return undefined;
    }

    const legacy = LEGACY_OF[to?.name];

    if (legacy) {
        return { name: legacy, params: to.params, query: to.query };
    }

    return { name: 'gradebook-view', params: to.params, query: to.query };
}
